import React, { useEffect, useState } from "react";
import PanelDashboardLayout from "../PanelDashboardLayout";
import { useNavigate, useParams } from "react-router-dom";
import { getStudentPromotionHistoryViaAdmin } from "../../../utils/Api";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Chip from "@mui/material/Chip";

const outcomeColor = (outcome) => {
  switch ((outcome || "").toLowerCase()) {
    case "promoted":
      return "success";
    case "detained":
      return "error";
    case "graduated":
      return "info";
    default:
      return "default";
  }
};

const StudentPromotionHistory = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { studentId } = useParams();

  useEffect(() => {
    fetchHistory();
  }, [studentId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await getStudentPromotionHistoryViaAdmin(studentId);
      setData(response || null);
    } catch (error) {
      console.error("Error fetching student history:", error);
      toast.error(error?.response?.data?.error || "Error loading student history");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PanelDashboardLayout>
        <div className="text-center p-5">Loading...</div>
      </PanelDashboardLayout>
    );
  }

  if (!data) {
    return (
      <PanelDashboardLayout>
        <div className="text-center p-5">
          <p>Student history not found.</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </PanelDashboardLayout>
    );
  }

  const student = data.student || {};
  const enrollments = data.enrollments || [];
  const events = data.events || [];
  const studentDetails = student?.studentDetails || {};
  const studentName =
    [studentDetails.firstName, studentDetails.middleName, studentDetails.lastName]
      .filter(Boolean)
      .join(" ") || "Student";
  const rollNumber = student?.academicDetails?.rollNumber;

  return (
    <PanelDashboardLayout>
      <div>
        <button className="btn btn-outline-secondary mb-3" onClick={() => navigate(-1)}>
          <ArrowBackIcon fontSize="small" /> Back
        </button>

        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-0">{studentName}</h4>
              <p className="card-description">
                {rollNumber ? `Roll No: ${rollNumber}` : ""}
              </p>
            </div>
          </div>
        </div>

        {/* Enrollment timeline */}
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Enrollment History ({enrollments.length})</h4>
              <div className="table-responsive">
                <table className="table table-striped mb-0">
                  <thead>
                    <tr>
                      <th>Academic Year</th>
                      <th>Year Stage</th>
                      <th>Year No</th>
                      <th>Division</th>
                      <th>Status</th>
                      <th>Exit Type</th>
                      <th>Attempt</th>
                      <th>Backlog @ Entry</th>
                      <th>Current</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((en, idx) => (
                      <tr
                        key={idx}
                        style={{
                          backgroundColor: en.isCurrent ? "#e7f3ff" : "inherit",
                          fontWeight: en.isCurrent ? 600 : "normal",
                        }}
                      >
                        <td>
                          {typeof en.academicYear === "object"
                            ? en.academicYear?.name || "-"
                            : en.academicYear || "-"}
                        </td>
                        <td>{en.yearStage || "-"}</td>
                        <td>{en.yearNumber ?? "-"}</td>
                        <td>{en.division || "-"}</td>
                        <td>{en.status || "-"}</td>
                        <td>{en.exitType || "-"}</td>
                        <td>{en.attemptNo ?? "-"}</td>
                        <td>{en.backlogCountAtEntry ?? "-"}</td>
                        <td>
                          {en.isCurrent ? (
                            <Chip label="Current" size="small" color="primary" />
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                    {enrollments.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center p-3">
                          No enrollment records.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Promotion events */}
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Promotion Events ({events.length})</h4>
              <div className="table-responsive">
                <table className="table table-striped mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Outcome</th>
                      <th>From → To</th>
                      <th>Backlog</th>
                      <th>Reason</th>
                      <th>Decided At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>
                          <Chip label={ev.outcome} size="small" color={outcomeColor(ev.outcome)} />
                        </td>
                        <td>
                          {ev.fromYearStage} &rarr; {ev.toYearStage || "-"}
                        </td>
                        <td>{ev.backlogCountAtDecision ?? "-"}</td>
                        <td style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                          {ev.reason}
                        </td>
                        <td>{ev.decidedAt ? new Date(ev.decidedAt).toLocaleString() : "-"}</td>
                      </tr>
                    ))}
                    {events.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center p-3">
                          No promotion events.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PanelDashboardLayout>
  );
};

export default StudentPromotionHistory;
